import { assertUnreachable, createRandomId } from "./utils.mjs";
export const createStateInstance = ({ countryCode, currency, cartId, cartVersion, tokenString, language, siteUrl, checkoutPageUrl, orderConfirmationPageUrl, onUnexpectedError, onShowOrderConfirmation, onBeforePurchase, onRedirect, onPostForm, onElementFocus, checkoutApiBaseUrl, atlasSessionId, translateApiMessage, }) => {
    const listeners = [];
    const id = cartId !== null && cartId !== void 0 ? cartId : createRandomId();
    const version = cartVersion !== null && cartVersion !== void 0 ? cartVersion : createRandomId();
    const isNew = cartId ? false : true;
    let currentStateData = {
        id,
        version,
        createdAt: new Date().toISOString(),
        lines: [],
        giftCards: [],
        discountCodes: [],
        customer: {},
        fees: { shipping: undefined },
        isNew,
        // If we have a cart id we know that we'll load the cart
        isLoading: !isNew,
        leftToFreeShipping: null,
        bundleSavings: null,
        countryCode,
        currency,
        // We set these to false because at this point we can't know if the country code and currency are valid
        validCountryCode: false,
        validCurrency: false,
        isExport: false,
        language,
        siteUrl,
        checkoutPageUrl,
        orderConfirmationPageUrl,
        tokenString,
        // This is just the default state because at this point we don't know if we're ready to complete the purchase
        // or if the payment provider doesn't support completing the purchase by calling the SDKs completePurchase() method.
        completePurchaseReadyState: 'loading',
        checkoutElements: {
            paymentVerification: undefined,
            takeover: undefined,
            express: undefined,
        },
    };
    const instance = Object.assign(Object.assign({}, currentStateData), { onUnexpectedError,
        onShowOrderConfirmation,
        onBeforePurchase,
        onRedirect,
        onPostForm,
        onElementFocus,
        checkoutApiBaseUrl, completePurchaseHandler: undefined, atlasSessionId,
        translateApiMessage,
        clear() {
            instance.update({
                id: createRandomId(),
                version: createRandomId(),
                createdAt: new Date().toISOString(),
                lines: [],
                giftCards: [],
                discountCodes: [],
                customer: {},
                isNew: true,
                isLoading: false,
                checkout: undefined,
                checkoutElements: {
                    paymentVerification: undefined,
                    takeover: undefined,
                    express: undefined,
                },
            });
        },
        loadingDone() {
            if (!instance.isLoading) {
                return Promise.resolve();
            }
            return new Promise((resolve) => {
                const listener = (state) => {
                    if (!state.isLoading) {
                        resolve();
                        unsubscribe();
                    }
                };
                const unsubscribe = instance.onChange(listener);
            });
        },
        update(newStateOrUpdater) {
            const oldState = currentStateData;
            let newState;
            if (typeof newStateOrUpdater === 'function') {
                const clonedState = JSON.parse(JSON.stringify(currentStateData));
                newStateOrUpdater(clonedState);
                newState = clonedState;
            }
            else {
                newState = newStateOrUpdater;
            }
            newState = hideApplePayIfNeeded(newState);
            Object.assign(instance, newState);
            currentStateData = Object.assign(Object.assign({}, currentStateData), newState);
            if (instance.isLoading) {
                instance.completePurchaseReadyState = 'loading';
            }
            else {
                instance.completePurchaseReadyState = readyState(currentStateData);
            }
            listeners.forEach((listener) => listener(instance, oldState));
        },
        onChange(callback) {
            listeners.push(callback);
            return () => {
                const index = listeners.indexOf(callback);
                if (index !== -1) {
                    listeners.splice(index, 1);
                }
            };
        } });
    return instance;
};
const hideApplePayIfNeeded = (state) => {
    var _a, _b;
    const supportsApplePay = !!(window === null || window === void 0 ? void 0 : window.ApplePaySession);
    if (!supportsApplePay) {
        const applePayId = 'applepay';
        if (((_b = (_a = state.checkout) === null || _a === void 0 ? void 0 : _a.payment) === null || _b === void 0 ? void 0 : _b.$type) === 'checkoutPage') {
            const applePayPaymentMethod = state.checkout.payment.paymentMethods.find((method) => method.id === applePayId);
            if (applePayPaymentMethod) {
                return Object.assign(Object.assign({}, state), { checkout: Object.assign(Object.assign({}, state.checkout), { payment: Object.assign(Object.assign({}, state.checkout.payment), { paymentMethods: state.checkout.payment.paymentMethods.filter((method) => method.id !== applePayId) }) }) });
            }
        }
    }
    return state;
};
const readyState = (state) => {
    if (!countryAndCurrencyIsValid(state)) {
        return 'invalidCountryOrCurrency';
    }
    const customerState = customerDataIsValid(state);
    if (customerState !== 'customerStateValid') {
        return customerState;
    }
    const vatNumberState = vatNumberIsValid(state);
    if (vatNumberState) {
        return vatNumberState;
    }
    return checkoutReadyState(state.checkout);
};
const vatNumberIsValid = (state) => {
    var _a, _b, _c;
    if (!((_a = state.customer.company) === null || _a === void 0 ? void 0 : _a.isCompany)) {
        return;
    }
    const vatNumberStatus = (_c = (_b = state.customer.company) === null || _b === void 0 ? void 0 : _b.vatNumber) === null || _c === void 0 ? void 0 : _c.isValid;
    if (vatNumberStatus && vatNumberStatus !== 'validated') {
        return 'invalidVatNumber';
    }
    return undefined;
};
const customerDataIsValid = (state) => {
    var _a, _b;
    const customer = state.customer;
    if (!customer) {
        return 'invalidCustomerData';
    }
    const customerIsValid = !!customer.email && !!((_a = customer.address) === null || _a === void 0 ? void 0 : _a.isValid) && (!customer.useBillingAddress || !!((_b = customer.billingAddress) === null || _b === void 0 ? void 0 : _b.isValid));
    if (!customerIsValid) {
        return 'invalidCustomerData';
    }
    return 'customerStateValid';
};
const countryAndCurrencyIsValid = (state) => {
    return state.validCountryCode && state.validCurrency;
};
const checkoutReadyState = (checkout) => {
    const shipping = checkout === null || checkout === void 0 ? void 0 : checkout.shipping;
    if ((shipping === null || shipping === void 0 ? void 0 : shipping.$type) === 'checkoutPage' && !shipping.selectedShippingMethod) {
        return 'missingShippingMethod';
    }
    const payment = checkout === null || checkout === void 0 ? void 0 : checkout.payment;
    if (!payment) {
        return 'missingPaymentMethod';
    }
    if (payment.$type === 'checkoutPage') {
        return payment.selectedPaymentMethod ? 'ready' : 'missingPaymentMethod';
    }
    if (payment.$type === 'free' || payment.$type === 'stripeCheckout') {
        return 'ready';
    }
    if (payment.$type === 'alreadyPaid') {
        return 'alreadyPaid';
    }
    assertUnreachable(payment);
    // We won't reach this point, but we need to return something to satisfy the type checker
    return 'loading';
};
//# sourceMappingURL=state.js.map